import { SearchableSelect } from "@/components/molecules/searchable-select";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useAdminTenants,
  useProvisionUser,
  type ClientRole,
  type ProvisionResult,
} from "@/features/admin";
import { ArrowsClockwise } from "@phosphor-icons/react";
import { useState } from "react";
import { toast } from "sonner";

/** Same alphabet as the server generator — no 0/O/1/l/I, the password gets read out loud. */
const ALPHABET = "abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";

const generatePassword = (length = 16) => {
  const bytes = new Uint32Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => ALPHABET[b % ALPHABET.length]).join("");
};

type Mode = "new" | "existing";

/**
 * SYSADMIN account creation: pick the tenant (new or existing), the email and the password,
 * and hand the credentials over directly — no email OTP, no invitation to accept.
 */
export function ProvisionForm({
  onCreated,
  onTenantChange,
}: {
  onCreated: (result: ProvisionResult) => void;
  onTenantChange?: (tenantId: string) => void;
}) {
  const [mode, setMode] = useState<Mode>("new");
  const [clientId, setClientId] = useState("");
  const [clientName, setClientName] = useState("");
  const [language, setLanguage] = useState("fr");
  const [role, setRole] = useState<ClientRole>("OWNER");
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  // Empty by default: the server generates one for a new account and leaves an existing
  // account's password alone. A pre-filled value was sent as "typed by staff" and silently
  // replaced the password of every existing user added to a tenant.
  const [password, setPassword] = useState("");

  const { data: tenants = [], isLoading: tenantsLoading } =
    useAdminTenants(365);
  const provision = useProvisionUser();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (mode === "new" && !clientName.trim()) {
      toast.error("Le nom du nouveau tenant est requis");
      return;
    }
    if (mode === "existing" && !clientId) {
      toast.error("Sélectionnez un tenant");
      return;
    }
    if (password && password.length < 8) {
      toast.error("Le mot de passe doit faire au moins 8 caractères");
      return;
    }

    try {
      const result = await provision.mutateAsync({
        email: email.trim(),
        password: password || undefined,
        full_name: fullName.trim() || undefined,
        lang: mode === "new" ? language : undefined,
        client_id: mode === "existing" ? clientId : undefined,
        client_name: mode === "new" ? clientName.trim() : undefined,
        default_language: mode === "new" ? language : undefined,
        role: mode === "existing" ? role : undefined,
      });

      toast.success("Accès créé");
      onCreated(result);
      onTenantChange?.(result.client.id);

      // Keep the tenant selection so several users can be created in a row.
      setMode("existing");
      setClientId(result.client.id);
      setClientName("");
      setEmail("");
      setFullName("");
      setPassword("");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Échec de la création",
      );
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Créer un accès client</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="mode">Tenant</Label>
            <Select value={mode} onValueChange={(v: Mode) => setMode(v)}>
              <SelectTrigger id="mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="new">Nouveau tenant</SelectItem>
                <SelectItem value="existing">Tenant existant</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {mode === "new" ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="client-name">Nom de l'entreprise</Label>
                <Input
                  id="client-name"
                  placeholder="Acme"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  L'utilisateur en devient automatiquement OWNER.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="language">Langue par défaut</Label>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger id="language">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fr">Français</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          ) : (
            <>
              <div className="space-y-2">
                <Label>Tenant existant</Label>
                <SearchableSelect
                  variant="select"
                  items={tenants.map((t) => ({ id: t.id, label: t.name }))}
                  value={clientId}
                  onChange={(v) => {
                    const next = Array.isArray(v) ? v[0] : v;
                    setClientId(next);
                    onTenantChange?.(next);
                  }}
                  isLoading={tenantsLoading}
                  placeholder="Sélectionner un tenant"
                  searchPlaceholder="Rechercher…"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Rôle dans le tenant</Label>
                <Select
                  value={role}
                  onValueChange={(v: ClientRole) => setRole(v)}
                >
                  <SelectTrigger id="role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="OWNER">Propriétaire</SelectItem>
                    <SelectItem value="AGENT">Agent</SelectItem>
                    <SelectItem value="NONE">Aucun</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="contact@acme.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="full-name">Nom complet (optionnel)</Label>
            <Input
              id="full-name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Mot de passe (optionnel)</Label>
            <div className="flex gap-2">
              <Input
                id="password"
                className="font-mono"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={8}
                placeholder="Généré automatiquement"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => setPassword(generatePassword())}
                title="Générer un mot de passe"
              >
                <ArrowsClockwise className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Vide : un mot de passe est généré pour un nouveau compte, et un
              compte existant garde le sien. Rempli (8 caractères minimum) : il
              remplace celui du compte.
            </p>
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={provision.isPending}
          >
            {provision.isPending ? "Création…" : "Créer l'accès"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
